// Sous-navigation des finances (vues de l'écran de référence S11) et droits du contexte actif sur
// les domaines recette et caisse ; un écran n'apparaît que si son domaine est lisible.
import { Lien } from '@cc/ui';
import { useLocation } from 'react-router';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import styles from './recettes.module.css';

export interface DroitsRecettes {
  voir: boolean;
  creer: boolean;
  modifier: boolean;
  decider: boolean;
  exporter: boolean;
  caisseVoir: boolean;
  caisseCreer: boolean;
  caisseModifier: boolean;
  /** Appartenance active (régisseur d'une caisse, quatre yeux). */
  moi: string | null;
}

export function useDroitsRecettes(): DroitsRecettes {
  const { contexte } = useSession();
  return {
    voir: peut(contexte, 'recette', 'voir'),
    creer: peut(contexte, 'recette', 'creer'),
    modifier: peut(contexte, 'recette', 'modifier'),
    decider: peut(contexte, 'recette', 'decider'),
    exporter: peut(contexte, 'recette', 'exporter'),
    caisseVoir: peut(contexte, 'caisse', 'voir'),
    caisseCreer: peut(contexte, 'caisse', 'creer'),
    caisseModifier: peut(contexte, 'caisse', 'modifier'),
    moi: contexte?.appartenance_active?.id ?? null,
  };
}

const VUES: { href: string; libelle: string; domaine: 'recette' | 'caisse' }[] = [
  { href: '/recettes', libelle: 'Vue d\'ensemble', domaine: 'recette' },
  { href: '/recettes/creances', libelle: 'Créances', domaine: 'recette' },
  { href: '/recettes/paiements', libelle: 'Paiements', domaine: 'recette' },
  { href: '/recettes/quittances', libelle: 'Quittances', domaine: 'recette' },
  { href: '/recettes/caisses', libelle: 'Caisses', domaine: 'caisse' },
  { href: '/recettes/remboursements', libelle: 'Remboursements', domaine: 'recette' },
  { href: '/recettes/rapprochements', libelle: 'Rapprochements', domaine: 'recette' },
  { href: '/recettes/anomalies', libelle: 'Anomalies', domaine: 'recette' },
  { href: '/recettes/catalogue', libelle: 'Catalogue et tarifs', domaine: 'recette' },
];

/** Vue active d'après le chemin : la plus longue entrée dont le chemin est un préfixe. */
export function vueActive(chemin: string): string {
  const base = chemin.replace(/\/$/, '') || '/';
  return VUES.filter((v) => base === v.href || base.startsWith(`${v.href}/`)).sort((a, b) => b.href.length - a.href.length)[0]?.href ?? '/recettes';
}

export function NavigationFinances() {
  const droits = useDroitsRecettes();
  const { pathname } = useLocation();
  const actif = vueActive(pathname);
  const visibles = VUES.filter((v) => (v.domaine === 'caisse' ? droits.caisseVoir : droits.voir));
  return (
    <nav aria-label="Vues des finances">
      <ul className={styles.sousNav}>
        {visibles.map((v) => (
          <li key={v.href}>
            <Lien href={v.href} aria-current={v.href === actif ? 'page' : undefined}>
              {v.libelle}
            </Lien>
          </li>
        ))}
      </ul>
    </nav>
  );
}
